import React, { useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * Reusable "review before print" dialog. Shows a PDF blob (via an object URL) in an iframe so the
 * user can eyeball it, then Print or Download straight from the preview. Callers fetch the PDF as a
 * blob, create an object URL, and pass it as `url`; revoke the URL in `onClose`.
 */
export default function PdfPreviewDialog({ open, onClose, url, title = 'Preview', fileName = 'document.pdf' }) {
  const iframeRef = useRef(null);

  const print = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) { win.focus(); win.print(); }
  };

  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        {title}
        <IconButton onClick={onClose} size="small" aria-label="Close preview"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '75vh' }}>
        {url && (
          <Box
            component="iframe"
            ref={iframeRef}
            title={title}
            src={url}
            sx={{ width: '100%', height: '100%', border: 0 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button startIcon={<DownloadIcon />} onClick={download} disabled={!url}>Download</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={print} disabled={!url}>Print</Button>
      </DialogActions>
    </Dialog>
  );
}
